import { ButtonVariants, useGlobalDialog } from '@talos/kyoko';
import { useCallback } from 'react';

/**
 * Hook that provides a simplified API for delete confirmation dialogs.
 * Wraps useGlobalDialog with pre-configured settings for delete operations.
 *
 * @example
 * ```tsx
 * const { confirmDelete } = useDeleteConfirmation();
 *
 * const handleDeleteUser = () => {
 *   confirmDelete({
 *     title: 'Delete User',
 *     itemName: `user "${user.name}"`,
 *     onConfirm: () => {
 *       deleteUser(user.id);
 *     },
 *   });
 * };
 * ```
 */
export function useDeleteConfirmation() {
  const { open: openDialog } = useGlobalDialog();

  const confirmDelete = useCallback(
    (options: { title?: string; itemName: string; onConfirm: () => void }) => {
      const { title = 'Delete', itemName, onConfirm } = options;

      openDialog({
        title,
        content: `Are you sure you want to delete ${itemName}?`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        variant: ButtonVariants.Negative,
        onConfirm,
      });
    },
    [openDialog]
  );

  return { confirmDelete };
}
